import { useMemo, useState } from "react";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useTranslation } from "react-i18next";
import { Search, BookOpen, Download, Users, CreditCard, Award, ArrowRight } from "lucide-react";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function SupportPublic() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");

  // Reuse the FAQ items from marketing.faq.q1..q8 / a1..a8
  const items = useMemo(() => (
    Array.from({ length: 8 }, (_, i) => ({
      q: t(`marketing.faq.q${i + 1}`),
      a: t(`marketing.faq.a${i + 1}`),
    }))
  ), [t]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) => it.q.toLowerCase().includes(needle) || it.a.toLowerCase().includes(needle));
  }, [items, q]);

  const categories = [
    { Icon: BookOpen, title: t("marketing.support.cat.bootcamps.title", "Bootcamps"), desc: t("marketing.support.cat.bootcamps.desc", "Enrolment, sessions, certificates."), href: "/faq#bootcamps" },
    { Icon: Download, title: t("marketing.support.cat.downloads.title", "Downloads"), desc: t("marketing.support.cat.downloads.desc", "Access, formats, licensing."), href: "/faq#downloads" },
    { Icon: Users, title: t("marketing.support.cat.coaching.title", "Coaching"), desc: t("marketing.support.cat.coaching.desc", "Booking, retainers, refunds."), href: "/faq#coaching" },
    { Icon: CreditCard, title: t("marketing.support.cat.payments.title", "Payments"), desc: t("marketing.support.cat.payments.desc", "Cards, mobile money, VAT."), href: "/faq#payments" },
    { Icon: Award, title: t("marketing.support.cat.certs.title", "Certificates"), desc: t("marketing.support.cat.certs.desc", "Issuing, verifying, sharing."), href: "/faq#certificates" },
  ];

  return (
    <PublicShell>
      <Seo title={t("pages.supportPub.seoTitle")} description={t("pages.supportPub.seoDescription")} />

      <section className="border-b border-border">
        <div className="container py-16 md:py-20 text-center">
          <Badge variant="secondary" className="mb-4">{t("pages.supportPub.badge")}</Badge>
          <h1 className="font-display text-4xl font-bold md:text-5xl text-balance">{t("pages.supportPub.title")}</h1>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground md:text-lg">{t("pages.supportPub.subtitle")}</p>
          <div className="relative mx-auto mt-8 max-w-xl">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("pages.supportPub.searchPlaceholder")}
              className="pl-9"
              aria-label={t("pages.supportPub.searchPlaceholder")}
            />
          </div>

          {q.trim() && (
            <div className="mx-auto mt-8 max-w-2xl text-left">
              {filtered.length === 0 ? (
                <p className="rounded-lg border border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">{t("pages.supportPub.noResults")}</p>
              ) : (
                <Accordion type="single" collapsible className="w-full">
                  {filtered.map((it, i) => (
                    <AccordionItem key={i} value={`s-${i}`}>
                      <AccordionTrigger className="text-left">{it.q}</AccordionTrigger>
                      <AccordionContent>{it.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="border-b border-border bg-muted/30">
        <div className="container py-16">
          <h2 className="mb-8 font-display text-2xl font-bold">{t("pages.supportPub.browseTitle")}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map(({ Icon, title, desc, href }) => (
              <Link key={title} to={href} className="group">
                <Card className="h-full transition group-hover:-translate-y-0.5 group-hover:border-primary/50">
                  <CardContent className="p-6">
                    <div className="mb-3 grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="font-display text-lg font-semibold group-hover:text-primary">{title}</div>
                    <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-primary/10 to-background">
        <div className="container py-16 text-center">
          <h2 className="font-display text-2xl font-bold">{t("pages.supportPub.stuckTitle")}</h2>
          <p className="mx-auto mt-2 max-w-xl text-muted-foreground">{t("pages.supportPub.stuckSubtitle")}</p>
          <Button asChild size="lg" className="mt-6">
            <Link to="/contact">{t("pages.supportPub.contactCta")}<ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>
    </PublicShell>
  );
}
